import { useQuery } from '@tanstack/react-query';
import type {
  TransactionLogDetail,
  TransactionLogEntry,
} from '@/components/transactions/mockTransactionLog';
import { httpClient } from '@/utils/httpClient';

interface TransactionLogResponse {
  transactions: TransactionLogEntry[];
  meta: {
    limit: number;
    count: number;
  };
}

export const useTransactionLog = (options?: {
  limit?: number;
  from?: string;
  to?: string;
  types?: string[];
  handReceiptTypes?: Array<'PHR' | 'THR'>;
  memberId?: string;
  enabled?: boolean;
}) => {
  const typesKey = options?.types?.join(',') ?? '';
  const handReceiptTypesKey = options?.handReceiptTypes?.join(',') ?? '';
  return useQuery({
    queryKey: [
      'transaction-log',
      options?.limit,
      options?.from ?? null,
      options?.to ?? null,
      typesKey,
      handReceiptTypesKey,
      options?.memberId ?? null,
    ],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<TransactionLogEntry[]> => {
      const params = new URLSearchParams();
      if (options?.limit) {
        params.set('limit', String(options.limit));
      }
      if (options?.from) {
        params.set('from', options.from);
      }
      if (options?.to) {
        params.set('to', options.to);
      }
      if (options?.types && options.types.length > 0) {
        params.set('types', options.types.join(','));
      }
      if (options?.handReceiptTypes && options.handReceiptTypes.length > 0) {
        params.set('handReceiptTypes', options.handReceiptTypes.join(','));
      }
      if (options?.memberId) {
        params.set('memberId', options.memberId);
      }
      const queryString = params.toString();
      const endpoint = `/api/rest/app/v1/transactions/log${queryString ? `?${queryString}` : ''}`;

      const response = await httpClient.get<TransactionLogResponse>(endpoint);
      return response.transactions;
    },
  });
};

export const useTransactionLogDetail = (transactionId: string | null) => {
  return useQuery({
    queryKey: ['transaction-log-detail', transactionId],
    enabled: !!transactionId,
    queryFn: async (): Promise<TransactionLogDetail> => {
      const response = await httpClient.get<{ transaction: TransactionLogDetail }>(
        `/api/rest/app/v1/transactions/log/${transactionId}`,
      );
      return response.transaction;
    },
  });
};
